import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "launcher-text" }
const _hoisted_2 = {
  key: 0,
  class: "chatbot-window"
}
const _hoisted_3 = { class: "chatbot-header" }
const _hoisted_4 = { class: "header-left" }
const _hoisted_5 = { class: "header-info" }
const _hoisted_6 = { class: "header-icon" }
const _hoisted_7 = { class: "header-title" }
const _hoisted_8 = { class: "header-actions" }
const _hoisted_9 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_10 = { key: 0 }
const _hoisted_11 = {
  key: 1,
  class: "typing-indicator"
}
const _hoisted_12 = ["innerHTML"]
const _hoisted_13 = ["innerHTML"]
const _hoisted_14 = { class: "input-area" }
const _hoisted_15 = ["placeholder", "disabled"]
const _hoisted_16 = ["disabled"]

import { ref, nextTick, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { marked } from "marked";
import hljs from "highlight.js";
import "highlight.js/styles/github-dark.min.css";
import { useChatStore } from "@/store/chat.store";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ChatBot',
  props: {
    apiUrl: { default: "" },
    placeholder: { default: "Sorunuzu buraya yazın..." },
    sendButtonText: { default: "Gönder" },
    conversationId: { default: "" },
    title: { default: "Delphin" },
    mode: { default: "floating" },
    defaultOpen: { type: Boolean, default: false }
  },
  emits: ["update:conversationId", "update:open", "message-sent", "message-received", "error"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const chatStore = useChatStore();
const { messages, isOpen, isLoading, isStreaming, conversationId } =
  storeToRefs(chatStore);

const inputMessage = ref<string>("");
const chatContainer = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

onMounted(() => {
  if (props.defaultOpen) {
    chatStore.setOpen(true);
  }
  if (props.conversationId) {
    chatStore.conversationId = props.conversationId;
  }
  if (isOpen.value) {
    nextTick(() => {
      scrollToBottom();
    });
  }
});

watch(conversationId, (newId) => {
  if (newId) {
    emit("update:conversationId", newId);
  }
});

watch(isOpen, (newVal) => {
  emit("update:open", newVal);
  if (newVal) {
    nextTick(() => {
      scrollToBottom();
      inputRef.value?.focus();
    });
  }
});

const toggleOpen = () => {
  chatStore.toggleOpen();
  if (isOpen.value) {
    nextTick(() => {
      scrollToBottom();
      inputRef.value?.focus();
    });
  }
};

const scrollToBottom = async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

/**
 * Yarım kalan inline markdown tokenlarını kapatır.
 */
function healMarkdown(fragment: string): string {
  let s = fragment;

  // Çift yıldız (bold): çift sayıda ** olmalı
  const boldCount = (s.match(/\*\*/g) || []).length;
  if (boldCount % 2 !== 0) s += "**";

  // Tek yıldız (italic): ** dışındaki * çift sayıda olmalı
  const italicCount = (s.replace(/\*\*/g, "").match(/\*/g) || []).length;
  if (italicCount % 2 !== 0) s += "*";

  // Backtick (inline code veya kod bloğu)
  const btCount = (s.match(/`/g) || []).length;
  if (btCount % 2 !== 0) s += "`";

  // Açık köşeli parantez (link / image başlangıcı)
  const openBrackets = (s.match(/\[/g) || []).length;
  const closeBrackets = (s.match(/\]/g) || []).length;
  if (openBrackets > closeBrackets) s += "](.)";

  return s;
}

/**
 * Streaming sırasında partial markdown'ı render eder.
 */
function renderStreamingMarkdown(text: string): string {
  const lastBoundary = text.lastIndexOf("\n\n");

  let completeText = "";
  let incompleteTail = text;

  if (lastBoundary !== -1) {
    completeText = text.slice(0, lastBoundary + 2);
    incompleteTail = text.slice(lastBoundary + 2);
  }

  const completeHtml = completeText
    ? (marked.parse(completeText) as string)
    : "";
  const tailHtml = incompleteTail
    ? (marked.parse(healMarkdown(incompleteTail)) as string)
    : "";

  return completeHtml + tailHtml;
}

const sendMessage = async () => {
  if (!inputMessage.value.trim() || isLoading.value) return;

  const currentMessage = inputMessage.value;
  inputMessage.value = "";

  const messagePromise = chatStore.sendMessage(currentMessage, props.apiUrl, {
    onMessageSent: (msg) => emit("message-sent", msg),
    onMessageReceived: (msg) => emit("message-received", msg),
    onError: (err) => emit("error", err),
  });

  await scrollToBottom();
  await messagePromise;

  await nextTick();
  inputRef.value?.focus();
};

const clearMessages = () => {
  chatStore.clearMessages();
};

__expose({
  sendMessage,
  clearMessages,
  toggleOpen,
  messages,
  isLoading,
  isStreaming,
  isOpen,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      'chatbot-root',
      props.mode === 'floating' ? 'chatbot-floating' : 'chatbot-inline',
      { 'is-open': _unref(isOpen) },
    ])
  }, [
    (props.mode === 'floating' && !_unref(isOpen))
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "chatbot-launcher-btn",
          "aria-label": "Sohbeti Başlat",
          onClick: toggleOpen
        }, [
          _createElementVNode("span", _hoisted_1, _toDisplayString(props.title), 1),
          _cache[1] || (_cache[1] = _createElementVNode("span", { class: "launcher-desc" }, [
            _createTextVNode("Sana nasıl "),
            _createElementVNode("br"),
            _createTextVNode("yardımcı "),
            _createElementVNode("br"),
            _createTextVNode("olabilirim?")
          ], -1))
        ]))
      : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "chat-slide" }, {
      default: _withCtx(() => [
        (props.mode === 'inline' || _unref(isOpen))
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _cache[2] || (_cache[2] = _createElementVNode("img", {
                        src: "https://delphinpiraye.com/uploads/chatbot/delphin-logo.svg",
                        alt: "Sanal Asistan Delphin"
                      }, null, -1)),
                      _createElementVNode("h3", _hoisted_7, _toDisplayString(props.title.replace("🐬", "").trim()), 1)
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "header-btn",
                    title: "Sohbeti Temizle",
                    "aria-label": "Sohbeti Temizle",
                    onClick: clearMessages
                  }, _cache[3] || (_cache[3] = [
                    _createElementVNode("svg", {
                      xmlns: "http://www.w3.org/2000/svg",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2",
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      class: "clear-icon"
                    }, [
                      _createElementVNode("polyline", { points: "3 6 5 6 21 6" }),
                      _createElementVNode("path", { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" })
                    ], -1)
                  ])),
                  (props.mode === 'floating')
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        class: "header-btn",
                        title: "Kapat",
                        "aria-label": "Kapat",
                        onClick: toggleOpen
                      }, _cache[4] || (_cache[4] = [
                        _createElementVNode("svg", {
                          xmlns: "http://www.w3.org/2000/svg",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2",
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          class: "close-icon"
                        }, [
                          _createElementVNode("line", {
                            x1: "18",
                            y1: "6",
                            x2: "6",
                            y2: "18"
                          }),
                          _createElementVNode("line", {
                            x1: "6",
                            y1: "6",
                            x2: "18",
                            y2: "18"
                          })
                        ], -1)
                      ])))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("div", {
                ref_key: "chatContainer",
                ref: chatContainer,
                class: "chat-container"
              }, [
                (_unref(messages).length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _renderSlot(_ctx.$slots, "empty", {}, () => [
                        _cache[5] || (_cache[5] = _createElementVNode("p", null, "Selam ben DELPHIN🐬", -1)),
                        _cache[6] || (_cache[6] = _createElementVNode("p", null, [
                          _createTextVNode(" Dönüşüm yolculuğunda aradığını bulmana "),
                          _createElementVNode("br"),
                          _createTextVNode(" yardımcı olmak için buradayım. ")
                        ], -1)),
                        _cache[7] || (_cache[7] = _createElementVNode("p", null, [
                          _createTextVNode(" Eğitimleri birlikte keşfedebilir ya da nereden "),
                          _createElementVNode("br"),
                          _createTextVNode("başlayabileceğine bakabiliriz. ")
                        ], -1)),
                        _cache[8] || (_cache[8] = _createElementVNode("p", null, "Sana nasıl eşlik edebilirim?", -1))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(messages), (msg, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: _normalizeClass([
              'message-bubble',
              msg.role === 'user' ? 'user-message' : 'assistant-message',
            ])
                  }, [
                    (msg.role === 'user')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(msg.content), 1))
                      : (
                msg.role === 'assistant' &&
                _unref(isLoading) &&
                !_unref(isStreaming) &&
                index === _unref(messages).length - 1 &&
                msg.content === ''
              )
                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, _cache[9] || (_cache[9] = [
                            _createElementVNode("span", null, null, -1),
                            _createElementVNode("span", null, null, -1),
                            _createElementVNode("span", null, null, -1)
                          ])))
                        : (
                msg.role === 'assistant' &&
                _unref(isLoading) &&
                index === _unref(messages).length - 1 &&
                msg.content !== ''
              )
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 2,
                              class: "markdown-content streaming-content",
                              innerHTML: renderStreamingMarkdown(msg.content)
                            }, null, 8, _hoisted_12))
                          : (msg.role === 'assistant')
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 3,
                                class: "markdown-content",
                                innerHTML: _unref(marked).parse(msg.content)
                              }, null, 8, _hoisted_13))
                            : _createCommentVNode("", true)
                  ], 2))
                }), 128))
              ], 512),
              _createElementVNode("div", _hoisted_14, [
                _renderSlot(_ctx.$slots, "input-prefix"),
                _withDirectives(_createElementVNode("input", {
                  id: "chatbot-input",
                  ref_key: "inputRef",
                  ref: inputRef,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputMessage).value = $event)),
                  type: "text",
                  autocomplete: "off",
                  autocorrect: "off",
                  autocapitalize: "off",
                  spellcheck: "false",
                  placeholder: props.placeholder,
                  disabled: _unref(isLoading),
                  onKeyup: _withKeys(sendMessage, ["enter"])
                }, null, 40, _hoisted_15), [
                  [_vModelText, inputMessage.value]
                ]),
                _createElementVNode("button", {
                  id: "chatbot-send-button",
                  disabled: _unref(isLoading) || !inputMessage.value.trim(),
                  "aria-label": "Gönder",
                  onClick: sendMessage
                }, _cache[10] || (_cache[10] = [
                  _createElementVNode("img", {
                    src: "https://delphinpiraye.com/uploads/chatbot/button-send.png",
                    alt: "Mesajını göndermek için tıklayın",
                    width: "50",
                    height: "50"
                  }, null, -1)
                ]), 8, _hoisted_16),
                _renderSlot(_ctx.$slots, "input-suffix")
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 3
    })
  ], 2))
}
}

})